/**
 * Loyalty Progress Bar Custom Element
 * A reusable progress bar component for displaying tier progression.
 *
 * @example
 * <loyalty-progress-bar
 *   current-tier="stalking-wolf"
 *   next-tier="howling-wolf"
 *   points-earned="1240"
 *   points-to-next="760"
 * ></loyalty-progress-bar>
 */
class LoyaltyProgressBar extends HTMLElement {
  static get observedAttributes() {
    return [
      'points-earned',
      'points-to-next',
      'next-tier',
    ];
  }

  constructor() {
    super();
    this._initialized = false;
  }

  connectedCallback() {
    if (!this._initialized) {
      this._initialized = true;
      this.updateProgress();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this._initialized) {
      this.updateProgress();
      if (name === 'points-earned' || name === 'points-to-next') {
        this.updateAriaLabel();
      }
    }
  }

  get pointsEarned() {
    const raw = this.getAttribute('points-earned') || '0';
    return parseInt(raw.replace(/[^0-9]/g, ''), 10) || 0;
  }

  set pointsEarned(value) {
    this.setAttribute('points-earned', String(value));
  }

  get pointsToNext() {
    const raw = this.getAttribute('points-to-next') || '0';
    return parseInt(raw.replace(/[^0-9]/g, ''), 10) || 0;
  }

  set pointsToNext(value) {
    this.setAttribute('points-to-next', String(value));
  }

  get nextTier() {
    return this.getAttribute('next-tier') || '';
  }

  calculateProgressPercent() {
    const earned = this.pointsEarned;
    const toNext = this.pointsToNext;
    const total = earned + toNext;

    if (total <= 0) {
      return 0;
    }

    return Math.round(Math.max(0, Math.min(1, earned / total)) * 100);
  }

  updateProgress() {
    const fill = this.querySelector('.loyalty-progress-bar__fill');
    if (!fill) return;

    const percent = this.calculateProgressPercent();
    this.style.setProperty('--loyalty-progress-percent', `${percent}%`);
  }

  updateAriaLabel() {
    const pointsEarned = this.pointsEarned;
    const pointsToNext = this.pointsToNext;
    const nextTier = this.nextTier.replace(/-/g, ' ');
    const capitalizedTier = nextTier
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    this.setAttribute(
      'aria-label',
      `${pointsEarned} points earned, ${pointsToNext} points to ${capitalizedTier}.`
    );
  }

  /**
   * Update points from external source (e.g., Yotpo widget)
   * @param {number} earned - Points earned
   * @param {number} toNext - Points to next tier
   */
  setPoints(earned, toNext) {
    this.pointsEarned = earned;
    this.pointsToNext = toNext;
  }
}

if (!customElements.get('loyalty-progress-bar')) {
  customElements.define('loyalty-progress-bar', LoyaltyProgressBar);
}
